import {useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router';

import {useDocumentContext} from '@/components/document/context';
import {SOURCE_ACCOUNT_FIELD_ID} from '@/components/document/hooks/use-prefilled-source-investor-account';
import {COMP_FIELDS} from '@/constants/comp';
import {INVE_FIELDS} from '@/constants/inve';
import {PRV_COMP_ARRAYS, PRV_COMP_FIELDS} from '@/constants/prv-comp';
import {SECTION_I, SECTION_J_PLUS} from '@/constants/sections';
import {useConfig} from '@/contexts/config';
import {DocumentData, ValueResolver, computeDocumentModel} from '@/engine';
import {
  isReferenceToAccount,
  isReferenceToAccountMeta,
  isReferenceToInvestorBankAccount,
  isReferenceToInvestorProfile,
  isReferenceToSourceAccount,
  isReferenceToSourceAccountMeta,
} from '@/engine/utils/reference';
import {logger} from '@/initializers/logger';
import {Toaster} from '@/initializers/toaster';
import {Booleans} from '@/model/src/values/dv-boolean';
import {getInvestorAccount, getInvestorById} from '@/repositories/beneva-api/endpoints/client-info';
import {DetailedInvestorAccount} from '@/types/client-info';
import {mapAccountToCOMP, mapClientInfoToINVE} from '@/utils/client-info';
import {
  findSelectedSourceAccount,
  reorderAccountInvestments,
  reorderAccountPacs,
  reorderInvestor,
  resolveSelectorIndex,
} from '@/utils/client-info/account-refresh';
import {getInitialSection} from '@/utils/document/get-initial-section';
import {batchSetFields, clearFieldsByPredicate} from '@/utils/document/store-operations';
import {captureGroupedException} from '@/utils/sentry-helpers';
import {useClearArrayFields} from './use-clear-array-fields';
import {useDetectAndClearRemovedInvestments} from './use-detect-and-clear-removed-investments';

const LOG_PREFIX = '[useRefreshPrefilledTransaction]';

const isDomainValueEqual = (value: unknown, ref: string): boolean =>
  typeof value === 'object' && value !== null && '$ref' in value && (value as {$ref: string}).$ref === ref;

const hasFilledValue = (value: unknown): value is string => typeof value === 'string' && value !== '';

const resolveSourceAccountNo = (data: DocumentData['data'], valueResolver: ValueResolver): string | undefined => {
  const sourceAccountIndexValue = data[SOURCE_ACCOUNT_FIELD_ID];
  const sourceAccountIndex = sourceAccountIndexValue ? valueResolver(sourceAccountIndexValue) : '';
  return sourceAccountIndex === ''
    ? undefined
    : valueResolver({$ref: `${INVE_FIELDS.INVE43_existingAccountNo}#${sourceAccountIndex}`});
};

export const useRefreshPrefilledTransaction = (): void => {
  const {t} = useTranslation();
  const location = useLocation();
  const isNewDocument = location.state?.isNewDocument === true;

  const {model} = useConfig();
  const {store, valueResolver, setCurrentSectionId, document} = useDocumentContext();
  const detectAndClearRemovedInvestments = useDetectAndClearRemovedInvestments();
  const clearArrayFields = useClearArrayFields();

  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current || isNewDocument || document.status !== 'incomplete') return;
    hasRun.current = true;

    const initialData = store.getState().data;
    const investorNo = initialData[INVE_FIELDS.INVE0_investorNo];
    const isPrefilledClient = isDomainValueEqual(initialData[INVE_FIELDS.INVE_IsPrefilledClient], Booleans.True);

    if (!hasFilledValue(investorNo) || !isPrefilledClient) return;

    const compAccountNo = initialData[COMP_FIELDS.COMP1_accountNo] ?? initialData[INVE_FIELDS.INVE16_accountNo];
    const isPrefilledTransaction = hasFilledValue(compAccountNo);

    const prvCompAccountNo = resolveSourceAccountNo(initialData, valueResolver);
    const hasSourceAccount = hasFilledValue(prvCompAccountNo);

    const logContext = {investorNo, compAccountNo, prvCompAccountNo};

    const refresh = async (): Promise<void> => {
      logger.info(`${LOG_PREFIX} Refreshing prefilled transaction`, logContext);

      const [{data: investor}, prefilledAccountResponse] = await Promise.all([
        getInvestorById(investorNo),
        isPrefilledTransaction ? getInvestorAccount(investorNo, compAccountNo, true) : Promise.resolve(null),
      ]);

      const reorderedInvestor = reorderInvestor(investor, initialData);

      let hasBreakingChanges = false;

      const refreshInvestor = (freshAccount?: DetailedInvestorAccount): void => {
        clearFieldsByPredicate(
          store,
          key => isReferenceToInvestorProfile(key) && !isReferenceToInvestorBankAccount(key),
        );
        const investorData = freshAccount
          ? mapClientInfoToINVE(reorderedInvestor, freshAccount)
          : mapClientInfoToINVE(reorderedInvestor);
        batchSetFields(store, investorData);
      };

      const refreshTransactionAccount = (freshAccount: DetailedInvestorAccount): void => {
        const accountWithReorderedInvestments = reorderAccountInvestments(freshAccount, initialData, 'COMP');
        const reorderedAccount = reorderAccountPacs(accountWithReorderedInvestments, initialData, 'COMP');
        clearFieldsByPredicate(store, key => isReferenceToAccount(key) || isReferenceToAccountMeta(key));
        batchSetFields(store, mapAccountToCOMP(reorderedAccount));

        hasBreakingChanges =
          detectAndClearRemovedInvestments({oldData: initialData, freshAccount, prefix: 'COMP'}) || hasBreakingChanges;
      };

      const clearSourceAccountFields = (): void => {
        clearFieldsByPredicate(store, key => isReferenceToSourceAccount(key) || isReferenceToSourceAccountMeta(key));
      };

      const refreshSourceAccount = (freshSourceAccount: DetailedInvestorAccount): void => {
        const sourceAccountWithReorderedInvestments = reorderAccountInvestments(
          freshSourceAccount,
          initialData,
          'PRV_COMP',
        );
        const reorderedSourceAccount = reorderAccountPacs(
          sourceAccountWithReorderedInvestments,
          initialData,
          'PRV_COMP',
        );
        clearSourceAccountFields();
        batchSetFields(store, mapAccountToCOMP(reorderedSourceAccount, PRV_COMP_FIELDS, PRV_COMP_ARRAYS));

        hasBreakingChanges =
          detectAndClearRemovedInvestments({
            oldData: initialData,
            freshAccount: freshSourceAccount,
            prefix: 'PRV_COMP',
          }) || hasBreakingChanges;
      };

      const newSourceIndex = findSelectedSourceAccount(initialData, reorderedInvestor.accounts ?? []);
      const wasExistingContractRemoved = hasSourceAccount && newSourceIndex === null;

      if (wasExistingContractRemoved) {
        clearFieldsByPredicate(store, key => key === SOURCE_ACCOUNT_FIELD_ID);
        clearArrayFields([SECTION_I, SECTION_J_PLUS]);

        hasBreakingChanges = hasBreakingChanges || wasExistingContractRemoved;
      }

      if (isPrefilledTransaction && prefilledAccountResponse) {
        const {data: freshAccount} = prefilledAccountResponse;
        refreshInvestor(freshAccount);
        refreshTransactionAccount(freshAccount);
      } else {
        refreshInvestor();
      }

      if (newSourceIndex !== null) {
        const oldSourceIndex = resolveSelectorIndex(initialData[SOURCE_ACCOUNT_FIELD_ID]);
        if (newSourceIndex !== oldSourceIndex) {
          store.getState().updateField(SOURCE_ACCOUNT_FIELD_ID, {$template: newSourceIndex});
        }
      }

      if (hasSourceAccount && !wasExistingContractRemoved) {
        const {data: freshSourceAccount} = await getInvestorAccount(investorNo, prvCompAccountNo, true);
        refreshSourceAccount(freshSourceAccount);
      } else {
        // No valid source account (nothing selected, or its contract was removed above)
        clearSourceAccountFields();
      }

      if (hasBreakingChanges) {
        logger.info(`${LOG_PREFIX} Refreshed with breaking changes`, {...logContext, wasExistingContractRemoved});
        Toaster.warning(t('components.document.refresh-account.breaking-changes'));
      } else {
        logger.info(`${LOG_PREFIX} Refreshed successfully`, logContext);
      }

      // Recompute from the store: the context's documentModel isn't updated until the next render.
      const refreshedDocumentModel = computeDocumentModel(model, store, valueResolver);
      setCurrentSectionId(getInitialSection(refreshedDocumentModel, store.getState(), valueResolver));
    };

    refresh().catch(err => {
      logger.error(`${LOG_PREFIX} Error refreshing prefilled transaction:`, err);
      captureGroupedException(err instanceof Error ? err : new Error(String(err)), {
        fingerprint: ['refresh-prefilled-transaction-error'],
        tags: {component: 'useRefreshPrefilledTransaction'},
        extra: logContext,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should only run once
  }, []);
};
